import React, { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslate } from '../TranslationText';
import dayjs from 'dayjs';

/**
 * Format any date representation to DD/MM/YYYY for display.
 */
export const formatToDMY = (val) => {
    if (!val) return '';
    const clean = String(val).trim();
    const ymdMatch = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (ymdMatch) {
        const year = ymdMatch[1];
        const month = ymdMatch[2].padStart(2, '0');
        const day = ymdMatch[3].padStart(2, '0');
        return `${day}/${month}/${year}`;
    }
    const dmyMatch = clean.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmyMatch) {
        const day = dmyMatch[1].padStart(2, '0');
        const month = dmyMatch[2].padStart(2, '0');
        const year = dmyMatch[3];
        return `${day}/${month}/${year}`;
    }
    const d = dayjs(val);
    if (d.isValid()) {
        return d.format('DD/MM/YYYY');
    }
    return clean;
};

/**
 * Format any date representation to YYYY-MM-DD for native date input.
 */
export const formatToYMD = (val) => {
    if (!val) return '';
    const clean = String(val).trim();
    const dmyMatch = clean.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmyMatch) {
        const day = dmyMatch[1].padStart(2, '0');
        const month = dmyMatch[2].padStart(2, '0');
        const year = dmyMatch[3];
        return `${year}-${month}-${day}`;
    }
    const ymdMatch = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (ymdMatch) {
        const year = ymdMatch[1];
        const month = ymdMatch[2].padStart(2, '0');
        const day = ymdMatch[3].padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    const d = dayjs(val);
    if (d.isValid()) {
        return d.format('YYYY-MM-DD');
    }
    return '';
};

/**
 * Clean date input with React Hook Form integration that always displays in dd/MM/yyyy format.
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable input
 * @param {object} rules - Validation rules
 * @param {string} minDate - Minimum selectable date
 * @param {string} maxDate - Maximum selectable date
 * @param {boolean} hideLabel - Hide the label
 */
export default function FormDateInput({
    name,
    label,
    required = false,
    disabled = false,
    rules,
    minDate,
    maxDate,
    hideLabel = false,
    noMargin = false,
    hideError = false,
    noValidate = false,
}) {
    const { control, formState: { errors } } = useFormContext();
    const { translate } = useTranslate();
    const inputRef = useRef(null);

    const validationRules = {
        ...(required && !noValidate && {
            required: translate('web.requiredField', 'Required Field'),
        }),
        ...rules,
    };

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);

    const openPicker = () => {
        if (disabled) return;
        if (inputRef.current) {
            try {
                if (typeof inputRef.current.showPicker === 'function') {
                    inputRef.current.showPicker();
                } else {
                    inputRef.current.focus();
                }
            } catch (err) {
                inputRef.current.focus();
            }
        }
    };

    return (
        <div
            id={`field-container-${name}`}
            data-field={name}
            className={noMargin ? '' : 'mb-2'}
        >
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={validationRules}
                render={({ field }) => {
                    const displayVal = formatToDMY(field.value);
                    const inputVal = formatToYMD(field.value);

                    return (
                        <div style={{ position: 'relative', width: '100%' }}>
                            <div
                                id={`date-display-${name}`}
                                tabIndex={disabled ? -1 : 0}
                                role="button"
                                aria-label={label || name}
                                className={`form-control rounded dateDiv${fieldError ? ' is-invalid' : ''}`}
                                onClick={openPicker}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openPicker();
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: disabled ? '#e9ecef' : '#fff',
                                    minHeight: '38px',
                                    height: '38px',
                                    padding: '0 12px',
                                    userSelect: 'none',
                                    borderColor: fieldError ? '#dc3545' : undefined,
                                }}
                            >
                                <span
                                    style={{
                                        direction: 'ltr',
                                        unicodeBidi: 'bidi-override',
                                        color: displayVal ? '#212529' : '#6c757d',
                                        fontSize: '14px',
                                    }}
                                >
                                    {displayVal || 'dd/MM/yyyy'}
                                </span>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {Boolean(field.value) && !disabled && (
                                        <i
                                            className="fa fa-times"
                                            style={{
                                                color: '#999',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                field.onChange('');
                                            }}
                                            title={translate('web.clear', 'Clear')}
                                        />
                                    )}
                                    <i
                                        className="fa fa-calendar"
                                        style={{
                                            color: disabled ? '#aaa' : '#6c757d',
                                            fontSize: '15px',
                                        }}
                                    />
                                </div>
                            </div>

                            <input
                                type="date"
                                id={name}
                                name={name}
                                ref={(el) => {
                                    inputRef.current = el;
                                    field.ref(el);
                                }}
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    zIndex: -1,
                                }}
                                value={inputVal}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                }}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                min={formatToYMD(minDate)}
                                max={formatToYMD(maxDate)}
                                tabIndex={-1}
                            />
                        </div>
                    );
                }}
            />

            {!hideError && fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate('web.requiredField', 'Required Field')}
                </div>
            )}
        </div>
    );
}
